import { m, type Message } from '@/i18n';
import { JUDGMENTS, type Judgment } from './search-criteria';
import { errOf, errorMessage, isRecord, ok, type DomainError, type Result } from './types';

/**
 * The flow's stage-row vocabulary, the pure core (docs/FLOW.md): a flow
 * definition's payload carries an ordered list of stages, each with a gate on
 * its boundary. This file owns the row shape and its validation (used by
 * `parseFlowPayload` in `definitions.ts`); the membership/advance decisions
 * live in `domain/flows.ts`, the effects in `src/server/flows.ts`.
 *
 * The rows are authored definition only - no derived or per-member state (no
 * position, no gate status) is ever stored here; positions live on
 * `flow_memberships` and every fact is derived. (The pre-FLOW stage-children
 * model - stages materializing as child items, bindings on the rows - was
 * torn down in FLOW S8; docs/STAGE_GATES.md records it.)
 */

/** The protected-vs-open dial, per gate. `open` surfaces (flag, never wall);
 *  `enforced` forbids an early advance (gatekeepers pass, owners act through
 *  loudly at `critical`). */
export const GATE_MODES = ['open', 'enforced'] as const;
export type GateMode = (typeof GATE_MODES)[number];

/** A flow stage's work template (docs/FLOW.md): advancing into the stage
 *  auto-spawns every `mandatory: true` template as a child contract of the
 *  member (requester = the gate's gatekeeper, provenance = the advance, the
 *  spawned id appended to that gate's conditions). `mandatory: false` templates
 *  are offered by the UI, never auto-spawned. `key` is stable per row (dedupe /
 *  UI identity). (`type`, not `interface`, so rows assign to generic JSON.) */
export type FlowWorkTemplate = {
  key: string;
  title: string;
  mandatory: boolean;
  description?: string | null;
  acceptanceCriteria?: string | null;
  contributor?: string | null; // ref
  moduleData?: Record<string, unknown>;
};

export type StageGate = {
  mode: GateMode;
  /** Person or group ref; who may pass an enforced gate, and the REQUESTER of
   *  spawned mandatory work (the gatekeeper commissions and passes verdict).
   *  Default: the member's own requesters. */
  gatekeeper?: string | null;
  /** Item refs that must be `accepted` for this gate to pass (all-of). Carries
   *  Conditional-Go conditions; per-member additions live on the overlay. */
  condition?: string[];
  /** Flow-field keys (docs/FLOW.md) that must be filled on the member (under the
   *  flow's `flow:<flowId>` namespace) to ENTER this stage - compulsory info. */
  requiredFields?: string[];
  /** Per-stage work templates (docs/FLOW.md): the stage's package, spawned or
   *  offered on advance INTO this stage. */
  work?: FlowWorkTemplate[];
};

// --- The phase mapping: `means` pairs (docs/FLOW_DEFINITION.md §11) -----------

/** The three lifecycle atoms a pair may value (docs/CLOSED_AXIS.md). */
export const MEANS_ATOM_ATTRS = ['delivered', 'closed', 'active'] as const;
export type MeansAtomAttr = (typeof MEANS_ATOM_ATTRS)[number];

/** The two values a declared-signal pair may take (the canon: worn / clear). */
export const MEANS_SIGNAL_VALUES = ['worn', 'clear'] as const;
export type MeansSignalValue = (typeof MEANS_SIGNAL_VALUES)[number];

/**
 * One valued attribute pair of a stage's `means` (docs/FLOW_DEFINITION.md §11,
 * the settable-attribute canon - exactly these, the vocabulary is closed):
 * the three atoms (Tender/Retract, Close/Reopen, SetActive), the verdict
 * (Resolve/Accept/Dispute), a declared catalog tag worn or clear (the moving
 * user's declaration), or one of the flow's OWN fields at a typed value.
 * Nothing else exists: no priority, no seats, no position, no fact-mirrors.
 */
export type MeansPair =
  | { attr: MeansAtomAttr; value: boolean }
  | { attr: 'verdict'; value: Judgment }
  | { attr: 'signal'; key: string; value: MeansSignalValue }
  | { attr: 'field'; key: string; value: string | number };

/** A pair's dimension identity (one pair per dimension in a set). */
export function meansPairIdentity(pair: MeansPair): string {
  switch (pair.attr) {
    case 'signal':
      return `signal:${pair.key}`;
    case 'field':
      return `field:${pair.key}`;
    default:
      return pair.attr;
  }
}

/** 'off_track' -> 'Off track': a stable key worn as a word (§10.2: keys are
 *  never shown; the closest a payload-only surface gets to the catalog label). */
export function keyWord(value: string): string {
  const spaced = value.replaceAll('_', ' ').replaceAll('-', ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** THE plain-words rendering of one pair ("Done: yes", "Verdict:
 *  Accepted", "Blocked: worn") - the one vocabulary every surface speaks
 *  (spec lines, lane announcements, the validator's example states), as a
 *  descriptor the surface formats (docs/I18N.md §6). */
export function describeMeansPair(pair: MeansPair): Message {
  const yesNo = (value: boolean) => m(value ? 'attributes.pair.yes' : 'attributes.pair.no');
  switch (pair.attr) {
    case 'delivered':
      return m('attributes.pair.delivered', { value: yesNo(pair.value) });
    case 'closed':
      return m('attributes.pair.closed', { value: yesNo(pair.value) });
    case 'active':
      return m('attributes.pair.active', { value: yesNo(pair.value) });
    case 'verdict':
      return pair.value === 'none'
        ? m('attributes.pair.verdict.none')
        : pair.value === 'disputed'
          ? m('attributes.pair.verdict.disputed')
          : m('attributes.pair.verdict.accepted');
    case 'signal':
      return pair.value === 'worn'
        ? m('attributes.pair.signal.worn', { signal: keyWord(pair.key) })
        : m('attributes.pair.signal.clear', { signal: keyWord(pair.key) });
    case 'field':
      return pair.value === ''
        ? m('attributes.pair.field.empty', { field: keyWord(pair.key) })
        : m('attributes.pair.field', { field: keyWord(pair.key), value: String(pair.value) });
  }
}

/**
 * A stage's pairs side by side, its conclusion read as the ending it brings
 * (docs/CLOSED_AXIS.md "Finishing", 2026-11-03): `closed: yes` with
 * `delivered: yes` says Done, and `closed: yes` without a delivery says
 * Cancelled - what a closed-only stage does to work nobody handed in - so a
 * flow author reads what the stage does in the words the item will wear.
 * Every other pair keeps its own words. `undefined` for none.
 */
export function describeMeans(pairs: readonly MeansPair[]): Message | undefined {
  const atom = (attr: 'delivered' | 'closed') =>
    pairs.find((p): p is Extract<MeansPair, { value: boolean }> => p.attr === attr);
  const closed = atom('closed');
  if (!closed?.value) return joinPairs(pairs.map(describeMeansPair));
  const ending = m(
    atom('delivered')?.value
      ? 'attributes.pair.position.done'
      : 'attributes.pair.position.cancelled',
  );
  const rest = pairs.filter((p) => p.attr !== 'delivered' && p.attr !== 'closed');
  return joinPairs([ending, ...rest.map(describeMeansPair)]);
}

/** Described pairs side by side; `undefined` for none. */
export function joinPairs(parts: readonly Message[]): Message | undefined {
  if (parts.length === 0) return undefined;
  return parts.reduceRight((second, first) => m('attributes.pair.joined', { first, second }));
}

/** The attribute one pair values, by the name its described pair starts with
 *  ("Done", "Verdict", "Blocked"): what a refusal names when a set names
 *  one attribute twice. */
export function meansPairName(pair: MeansPair): Message | string {
  switch (pair.attr) {
    case 'delivered':
      return m('flowErrors.pairName.delivered');
    case 'closed':
      return m('flowErrors.pairName.closed');
    case 'active':
      return m('flowErrors.pairName.active');
    case 'verdict':
      return m('flowErrors.pairName.verdict');
    case 'signal':
    case 'field':
      return keyWord(pair.key);
  }
}

/** A refusal inside one stage, with the stage named (editor legibility). */
export function stageError(key: string, error: DomainError): Message {
  return m('flowErrors.stage.wrapped', { stage: key, error: errorMessage(error) });
}

/** Parse one `means` pair, strictly (the authoring path: malformed is an
 *  error, never a silent drop). */
export function parseMeansPair(value: unknown): Result<MeansPair> {
  if (!isRecord(value)) return errOf('invalid_input', m('flowErrors.means.notObject'));
  switch (value.attr) {
    case 'delivered':
    case 'closed':
    case 'active':
      if (typeof value.value !== 'boolean') {
        return errOf('invalid_input', m('flowErrors.means.needsYesNo', { attr: value.attr }));
      }
      return ok({ attr: value.attr, value: value.value });
    case 'verdict':
      if (!(JUDGMENTS as readonly unknown[]).includes(value.value)) {
        return errOf(
          'invalid_input',
          m('flowErrors.means.verdictValue', { values: JUDGMENTS.join(' | ') }),
        );
      }
      return ok({ attr: 'verdict', value: value.value as Judgment });
    case 'signal': {
      const key = typeof value.key === 'string' ? value.key.trim() : '';
      if (key.length === 0) return errOf('invalid_input', m('flowErrors.means.signalKey'));
      if (!(MEANS_SIGNAL_VALUES as readonly unknown[]).includes(value.value)) {
        return errOf('invalid_input', m('flowErrors.means.signalValue'));
      }
      return ok({ attr: 'signal', key, value: value.value as MeansSignalValue });
    }
    case 'field': {
      const key = typeof value.key === 'string' ? value.key.trim() : '';
      if (key.length === 0) return errOf('invalid_input', m('flowErrors.means.fieldKey'));
      const v = value.value;
      if (typeof v !== 'string' && typeof v !== 'number') {
        return errOf('invalid_input', m('flowErrors.means.fieldScalar'));
      }
      if (v === '') return errOf('invalid_input', m('flowErrors.means.fieldEmpty'));
      return ok({ attr: 'field', key, value: v });
    }
    default:
      return errOf('invalid_input', m('flowErrors.means.unknownAttr'));
  }
}

export type FlowPhaseRow = {
  key: string; // stable row id; positions and portfolio alignment key off it
  label: string;
  /** Optional human-authored note on what this stage is FOR (docs/FLOW.md
   *  "Authoring surfaces"): free prose the author writes in the editor, ADDITIVE
   *  to the machine-generated spec prose (never replaces it). Empty is stored as
   *  absent (never ''). Surfaced on the board lane's title tooltip. */
  description?: string;
  /** The stage's MEANING (docs/FLOW_DEFINITION.md §11): a set of valued
   *  attribute pairs over the settable-attribute canon. Moving a member here
   *  APPLIES exactly these pairs (each through its real act); an act that
   *  takes the ENTIRE set from not-holding to holding PULLS the member here
   *  (edge-triggered, machine provenance). Empty is legal: a purely manual
   *  word that applies nothing and never pulls. There is no platform phase
   *  vocabulary - the pairs ARE the stored meaning. */
  means: MeansPair[];
  gate: StageGate;
};

function optionalString(v: unknown, what: Message): Result<string | undefined> {
  if (v === undefined || v === null || v === '') return ok(undefined);
  if (typeof v !== 'string')
    return errOf('invalid_input', m('flowErrors.value.notString', { what }));
  return ok(v);
}

/** Validate one stage row. Returns the canonicalized row: trimmed key/label,
 *  defaulted gate mode, only known properties kept (unknown ones - including
 *  the pre-FLOW `itemId` binding and `spawn` template - are dropped, not
 *  stored). */
export function parseFlowPhaseRow(value: unknown): Result<FlowPhaseRow> {
  if (!isRecord(value)) return errOf('invalid_input', m('flowErrors.stage.notObject'));

  const key = typeof value.key === 'string' ? value.key.trim() : '';
  if (key.length === 0) return errOf('invalid_input', m('flowErrors.stage.needsKey'));
  const label = typeof value.label === 'string' ? value.label.trim() : '';
  if (label.length === 0)
    return errOf('invalid_input', m('flowErrors.stage.needsLabel', { stage: key }));

  // The optional human note (additive to generated prose): trimmed; empty is
  // dropped so we never store the '' noise.
  const description = typeof value.description === 'string' ? value.description.trim() : '';

  // The retired word alphabet is refused LOUDLY at the authoring path (never a
  // dual-shape reader; stored payloads were converted by the migration).
  if (value.spinePhase !== undefined) {
    return errOf('invalid_input', m('flowErrors.stage.spinePhaseRetired', { stage: key }));
  }

  // The means pairs (§11): default empty (a manual word); one pair per
  // dimension - a set naming one attribute twice is not a set.
  const means: MeansPair[] = [];
  if (value.means !== undefined && value.means !== null) {
    if (!Array.isArray(value.means)) {
      return errOf('invalid_input', m('flowErrors.stage.meansNotList', { stage: key }));
    }
    const seenDims = new Set<string>();
    for (const raw of value.means) {
      const pair = parseMeansPair(raw);
      if (!pair.ok) return errOf(pair.error.code, stageError(key, pair.error));
      const dim = meansPairIdentity(pair.value);
      if (seenDims.has(dim)) {
        return errOf(
          'invalid_input',
          m('flowErrors.stage.meansTwice', { stage: key, attribute: meansPairName(pair.value) }),
        );
      }
      seenDims.add(dim);
      means.push(pair.value);
    }
  }

  // Gate config: mode defaults to open (surface over forbid).
  const gateRaw = isRecord(value.gate) ? value.gate : {};
  const mode = gateRaw.mode === undefined ? 'open' : gateRaw.mode;
  if (mode !== 'open' && mode !== 'enforced') {
    return errOf('invalid_input', m('flowErrors.stage.gateMode', { stage: key }));
  }
  const gatekeeper = optionalString(
    gateRaw.gatekeeper,
    m('flowErrors.what.gatekeeper', { stage: key }),
  );
  if (!gatekeeper.ok) return gatekeeper;
  let condition: string[] | undefined;
  if (gateRaw.condition !== undefined && gateRaw.condition !== null) {
    if (
      !Array.isArray(gateRaw.condition) ||
      gateRaw.condition.some((c) => typeof c !== 'string' || c.trim().length === 0)
    ) {
      return errOf('invalid_input', m('flowErrors.stage.gateCondition', { stage: key }));
    }
    condition = gateRaw.condition.map((c) => (c as string).trim());
  }

  // Flow gate extensions (docs/FLOW.md): required flow-field keys + work templates.
  let requiredFields: string[] | undefined;
  if (gateRaw.requiredFields !== undefined && gateRaw.requiredFields !== null) {
    if (
      !Array.isArray(gateRaw.requiredFields) ||
      gateRaw.requiredFields.some((f) => typeof f !== 'string' || f.trim().length === 0)
    ) {
      return errOf('invalid_input', m('flowErrors.stage.requiredFields', { stage: key }));
    }
    requiredFields = gateRaw.requiredFields.map((f) => (f as string).trim());
  }
  let work: FlowWorkTemplate[] | undefined;
  if (gateRaw.work !== undefined && gateRaw.work !== null) {
    if (!Array.isArray(gateRaw.work)) {
      return errOf('invalid_input', m('flowErrors.stage.workNotList', { stage: key }));
    }
    work = [];
    const workKeys = new Set<string>();
    for (const raw of gateRaw.work) {
      if (!isRecord(raw)) {
        return errOf('invalid_input', m('flowErrors.stage.workNotObject', { stage: key }));
      }
      const wKey = typeof raw.key === 'string' ? raw.key.trim() : '';
      if (wKey.length === 0) {
        return errOf('invalid_input', m('flowErrors.stage.workNeedsKey', { stage: key }));
      }
      if (workKeys.has(wKey)) {
        return errOf(
          'invalid_input',
          m('flowErrors.stage.workDuplicateKey', { stage: key, template: wKey }),
        );
      }
      workKeys.add(wKey);
      const wTitle = typeof raw.title === 'string' ? raw.title.trim() : '';
      if (wTitle.length === 0) {
        return errOf(
          'invalid_input',
          m('flowErrors.stage.workNeedsTitle', { stage: key, template: wKey }),
        );
      }
      // The mandatory | suggested dial; omitted reads as suggested (never spawn
      // silently by default), anything non-boolean is a malformed payload.
      const mandatory = raw.mandatory === undefined ? false : raw.mandatory;
      if (typeof mandatory !== 'boolean') {
        return errOf(
          'invalid_input',
          m('flowErrors.stage.workMandatory', { stage: key, template: wKey }),
        );
      }
      const wDescription = optionalString(
        raw.description,
        m('flowErrors.what.workDescription', { stage: key }),
      );
      if (!wDescription.ok) return wDescription;
      const wCriteria = optionalString(
        raw.acceptanceCriteria,
        m('flowErrors.what.workCriteria', { stage: key }),
      );
      if (!wCriteria.ok) return wCriteria;
      const wContributor = optionalString(
        raw.contributor,
        m('flowErrors.what.workContributor', { stage: key }),
      );
      if (!wContributor.ok) return wContributor;
      if (raw.moduleData !== undefined && !isRecord(raw.moduleData)) {
        return errOf('invalid_input', m('flowErrors.stage.workSeeds', { stage: key }));
      }
      work.push({
        key: wKey,
        title: wTitle,
        mandatory,
        ...(wDescription.value !== undefined ? { description: wDescription.value } : {}),
        ...(wCriteria.value !== undefined ? { acceptanceCriteria: wCriteria.value } : {}),
        ...(wContributor.value !== undefined ? { contributor: wContributor.value } : {}),
        ...(raw.moduleData !== undefined
          ? { moduleData: raw.moduleData as Record<string, unknown> }
          : {}),
      });
    }
  }

  return ok({
    key,
    label,
    ...(description ? { description } : {}),
    means,
    gate: {
      mode,
      ...(gatekeeper.value !== undefined ? { gatekeeper: gatekeeper.value } : {}),
      ...(condition !== undefined ? { condition } : {}),
      ...(requiredFields !== undefined ? { requiredFields } : {}),
      ...(work !== undefined ? { work } : {}),
    },
  });
}

/** Validate a whole `stages` list: each row, plus unique keys (the stable ids
 *  positions and portfolio alignment hang off). Order is meaning (the spine is
 *  linear; branching lives in decomposition/nesting, docs/FLOW.md). */
export function parseFlowPhaseRows(value: unknown): Result<FlowPhaseRow[]> {
  if (!Array.isArray(value)) return errOf('invalid_input', m('flowErrors.stages.notList'));
  const rows: FlowPhaseRow[] = [];
  const seen = new Set<string>();
  for (const raw of value) {
    const row = parseFlowPhaseRow(raw);
    if (!row.ok) return row;
    if (seen.has(row.value.key)) {
      return errOf('invalid_input', m('flowErrors.stages.duplicateKey', { stage: row.value.key }));
    }
    seen.add(row.value.key);
    rows.push(row.value);
  }
  return ok(rows);
}
